
import { Options, mixins } from 'vue-class-component'
import CLoading from '@/components/common/ui/CLoading.vue'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'
import { Watch } from 'vue-property-decorator'
import { ACTION_ANSWER_REPORT, ACTION_FORM } from '@/store/actions'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import { IDateRangePicker, IForm, IFormDetail, IFormLIFFDetail, IFormReport } from '@/utils/types'
import { DatetimeUtils } from '@/utils/datetime'
import JsonCSV from '@/components/common/ui/JsonCSV.vue'
import { CSVHeader } from '@/utils/csv'
import TagContent from '@/components/common/ui/TagContent.vue'
import BaseFormMixin from '../common/mixins/BaseFormMixin.vue'
import { ASSETS_FORM_TYPE } from '@/utils/constants'

@Options({
  components: {
    TagContent,
    DateRangeSelectorV2,
    TestModeSelector,
    CLoading,
    'download-csv': JsonCSV,
  },
})
export default class FormReport extends mixins(BaseFormMixin) {
  loading = false
  isTestMode = false
  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  selectedForm: IForm | null = null

  reports: IFormReport[] = []

  csvHeader: CSVHeader | null = null
  jsonData = [{}]

  get dynamicColumns() {
    const baseColumns = [
      {
        name: 'updated_at',
        required: true,
        label: this.$t('label.updated_at'),
        align: 'left',
        field: 'updated_at',
      },
      {
        name: 'display_name',
        required: true,
        label: this.$t('label.audience_user.display_name'),
        align: 'left',
        field: 'display_name',
      },
    ]

    if (this.selectedForm && this.reports.length > 0) {
      const detailsKey = 'details'
      let details: IFormLIFFDetail[] | IFormDetail[] | undefined = []
      if (this.selectedForm.type === ASSETS_FORM_TYPE.LIFF) {
        details = this.selectedForm.liff_details
      } else {
        details = this.selectedForm.details
      }

      if (!details) return baseColumns

      for (const detail of details) {
        let key = detail.option_type
        if (this.selectedForm.type === ASSETS_FORM_TYPE.LIFF) {
          key = detail._id
        }

        baseColumns.push({
          name: key,
          required: false,
          label: this.getColumnLabel(key ?? ''),
          align: 'left',
          field: (row) => {
            const detailsData = row[detailsKey] || {}
            return detailsData[key ?? ''] || ''
          },
        } as any)
      }
    }

    return baseColumns
  }

  getColumnLabel(key: string) {
    if (!this.selectedForm) return null
    if (this.selectedForm.type === ASSETS_FORM_TYPE.LIFF) {
      if (!this.selectedForm.liff_details) return key
      for (const detail of this.selectedForm.liff_details) {
        if (detail._id === key) {
          return detail.field_name
        }
      }
      return key
    } else {
      const labelMap = {
        name_kanji: this.$t('label.common.form.form_field_type_name_kanji'),
        name_hira: this.$t('label.common.form.form_field_type_name_hira'),
        name_kana: this.$t('label.common.form.form_field_type_name_kana'),
        phone_number: this.$t('label.common.form.form_field_type_phone_number_dash'),
        phone_number_no_dash: this.$t('label.common.form.form_field_type_phone_number_no_dash'),
        address: this.$t('label.common.form.form_field_type_address'),
        postcode_dash: this.$t('label.common.form.form_field_type_postcode_dash'),
        postcode_no_dash: this.$t('label.common.form.form_field_type_postcode_no_dash'),
        email: this.$t('label.common.form.form_field_type_email'),
        freetext: this.$t('label.common.form.form_field_type_freetext'),
      }
      return labelMap[key] || key
    }
  }

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  get forms() {
    return this.$store.getters.forms
  }

  @Watch('selectedAppId', { immediate: true })
  async updateApi() {
    if (!this.selectedAppId) {
      return
    }

    this.dateRange = DatetimeUtils.getDateRange('last30Days')

    if (this.selectedForm) {
      await this.loadData()
    }
  }

  @Watch('isTestMode')
  @Watch('selectedForm')
  handleChangeTargetSetting() {
    // Only load data if a form is selected
    if (this.selectedForm) {
      this.loadData()
    } else {
      // Clear reports when no form is selected
      this.reports = []
    }
  }

  async loadData() {
    this.loading = true

    this.reports = await this.$store.dispatch(ACTION_ANSWER_REPORT.LOAD_USER_DETAILS, {
      app_id: this.selectedAppId,
      from_date: DatetimeUtils.formatDate(this.dateRange.startDate),
      to_date: DatetimeUtils.formatDate(this.dateRange.endDate),
      form_id: this.selectedForm?._id,
      form_type: this.selectedForm?.type,
      is_test_mode: this.isTestMode,
    })

    // /* eslint-disable quote-props */
    // // just for testing
    // this.reports = [
    //   {
    //     'details': {
    //       'freetext': 'Ggfg',
    //       'name_hira': 'や',
    //       'name_kanji': '子',
    //     },
    //     'liff_details': {
    //       'erHaBZsEwrruMh4azomzJ9': 'name_kanji_value',
    //       'mRfj5iLjSMLKtMKjTZHvmX': 'name_hira_test',
    //       'cHpSyiv24ntVTed7VjskqJ': 'freetext_value',
    //     },
    //     'display_name': 'Andrew Huynh',
    //     'updated_at': '2025-09-03 16:00:49',
    //     'user_id': 'Ud0ff3722ddcbd3fcb84ce7a005da61c5',
    //   },
    // ]

    this.loading = false
  }

  changeDateRange(value) {
    this.dateRange = value

    if (this.selectedForm) {
      this.loadData()
    }
  }

  exportFinished() {
    this.loading = false
  }

  onExportReport() {
    if (this.reports.length === 0) {
      this.$q.dialog({
        title: this.$t('error'),
        message: this.$t('messages.no_data_available'),
        persistent: true,
      })
      return
    }

    this.loading = true

    // Build CSV header dynamically
    const csvHeaders = {
      updated_at: this.$t('label.updated_at'),
      display_name: this.$t('label.audience_user.display_name'),
      user_id: 'User ID',
    }
    const detailsKey = 'details'
    if (this.selectedForm && this.reports.length > 0) {
      let details: IFormLIFFDetail[] | IFormDetail[] | undefined = []
      if (this.selectedForm.type === ASSETS_FORM_TYPE.LIFF) {
        details = this.selectedForm.liff_details
      } else {
        details = this.selectedForm.details
      }

      if (details) {
        for (const detail of details) {
          let key = detail.option_type ?? ''
          if (this.selectedForm?.type === ASSETS_FORM_TYPE.LIFF) {
            key = detail._id ?? ''
          }
          csvHeaders[key] = this.getColumnLabel(key)
        }
      }
    }

    this.csvHeader = new CSVHeader(csvHeaders)

    const data: any[] = []
    this.reports.forEach((item) => {
      const details = item[detailsKey] || {}
      const rowData: any = {
        updated_at: item.updated_at,
        display_name: item.display_name,
        user_id: item.user_id,
      }

      // Map data using option_type as key
      Object.keys(details).forEach((key) => {
        rowData[key] = details[key]
      })

      data.push(rowData)
    })

    this.jsonData = data

    this.$nextTick(() => {
      this.$refs.downloadCSV.generate()
    })
    this.loading = false
  }

  async created() {
    await this.$store.dispatch(ACTION_FORM.LOAD_ITEMS, {
      app_id: this.selectedAppId,
    })
  }
}
